import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PLATFORM_ID, Injectable, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap, share } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { formatPercent, formatPrice } from '../utils';
import { ShoppingCart } from '../model/shoppingCart.js';
import { CartItem } from '../model/cartItem.js';
import { GoogleAnalyticsService } from './google-analytics.service';
import { Product } from '../model/product';

@Injectable({ providedIn: 'root' })
export class ShoppingCartService {
  private platformId = inject<Record<string, unknown>>(PLATFORM_ID);
  private http = inject(HttpClient);
  private toasterService = inject(ToastrService);
  private logger = inject(NGXLogger);
  private googleAnalyticsService = inject(GoogleAnalyticsService);


  _shoppingCart$: Observable<ShoppingCart>;
  shoppingCart$ = new BehaviorSubject<ShoppingCart>(null);

  constructor() {

      this._shoppingCart$ = this.http.get<ShoppingCart>(this.url()).pipe(
        share(),
        tap((data: ShoppingCart) => {
          data.items.forEach(item => {
            Product.enrich(item.product);
          });
          data.itemsCount = data.items.length;
          data.formattedTotalAmount = formatPrice(data.totalAmount);
          data.formattedSalePercent = formatPercent(data.salePercent);
          this.shoppingCart$.next(data);
        })
      );
    }

  url = (): string => `${environment.api}/v1/shopping-cart`;
  cartItemUrl = (cartItemId: string): string => `${environment.api}/v1/shopping-cart/${cartItemId}`;

  get(): void {
    if(isPlatformBrowser(this.platformId)) {
      this._shoppingCart$.subscribe();
    }
  }

  public addToCart(cartItem: CartItem): void {
    this.logger.info('shopping-cart.service.add-to-cart: ' + JSON.stringify(cartItem));
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };
    this.http.post(this.url(), cartItem, { ...options, responseType: 'text' }).subscribe(() => {
      this.get();
      this.toasterService.success('Item added to your cart', 'Successful!');
      this.googleAnalyticsService.emitEvent('add_to_cart', 'ecommerce', cartItem.product.price);
    });
  }

  public updateCartItem(cartItem: CartItem): void {
    this.logger.info('shopping-cart.service.update-cart-item: ' + JSON.stringify(cartItem));
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };
    this.http.put(this.cartItemUrl(cartItem.id), cartItem, { ...options, responseType: 'text' }).subscribe(() => {
      this.get();
      this.toasterService.success('Item updated in your cart', 'Successful!');
    });
  }

  public removeFromCart(cartItem: CartItem): void {
    this.logger.info('shopping-cart.service.remove-from-cart: ' + JSON.stringify(cartItem));
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };
    this.http.delete(this.cartItemUrl(cartItem.id), { ...options, responseType: 'text' }).subscribe(() => {
      this.get();
      this.toasterService.success('Item removed from your cart', 'Successful!');
      this.googleAnalyticsService.emitEvent('remove_from_cart', 'ecommerce', cartItem.product.price);
    });
  }

  public setBillingCountry(country: string): void {
    this.logger.info('shopping-cart.service.set-billing-country: ' + JSON.stringify(country));

  }

}
